import {Fragment} from "react";
import {Table, TableBody, TableCell, TableRow} from "@mui/material";
import FormattedRegistrationTime from "../../../FormattedRegistrationTime";
import DeleteButton from "./DeleteButton";
import UpdateButton from "./save/UpdateButton";
import Tooltip from "./Tooltip";
import FormattedTableHead from "../../../FormattedTableHead";
import CreateButton from "./save/CreateButton";
import NoDeviceBlock from "../NoDeviceBlock";
import {HasPermission} from "../../../widgets";
import {ThirdPartyDevicesQueryHookResult} from "../queries.generated";
import {defineMessage} from "react-intl";
import {ContextFilters} from "@/context/AuthProvider";
import {useDomainNameQuery} from "@/hooks";

type Props = {
    extensionId: number;
    domainId: number;
    onMutate: () => void;
    query: ThirdPartyDevicesQueryHookResult;
    canAdd: boolean;
};

export default ({extensionId, domainId, onMutate, query, canAdd}: Props) => {
    const componentId = "extension-thirdparty-devices";

    const {domainName} = useDomainNameQuery(domainId);
    const domainFilter = ContextFilters.domain(domainName);

    function onSuccess() {
        onMutate();
        query.refetch();
    }

    if (!query.data.thirdPartyDevices.length) {
        return (
            <div id={componentId}>
                <NoDeviceBlock idSuffix="thirdparty">
                    <HasPermission name="manage_thirdparty_device" contextFilter={domainFilter}>
                        <CreateButton disabled={!canAdd} extensionId={extensionId} onSuccess={onSuccess} />
                    </HasPermission>
                </NoDeviceBlock>
            </div>
        );
    }

    return (
        <Fragment>
            <Table id={componentId}>
                <FormattedTableHead
                    cells={[
                        {label: defineMessage({id: "description", defaultMessage: "Description"})},
                        {label: defineMessage({id: "devices.registrationTime", defaultMessage: "Registered"})},
                        {
                            props: {align: "center"},
                            renderer: () => (
                                <HasPermission name="manage_provisioned_device" contextFilter={domainFilter}>
                                    <CreateButton disabled={!canAdd} extensionId={extensionId} onSuccess={onSuccess} />
                                </HasPermission>
                            ),
                        },
                    ]}
                />
                <TableBody>
                    {query.data.thirdPartyDevices.map((device) => (
                        <TableRow key={device.id}>
                            <TableCell data-path="thirdparty-devices.description" data-value={device.description}>
                                {device.description}
                            </TableCell>
                            <TableCell>
                                <FormattedRegistrationTime
                                    registrationTime={device.registrationTime}
                                    expirationTime={device.registrationExpireTime}
                                    clientInfo={device.clientInfo}
                                />
                            </TableCell>
                            <TableCell align="center">
                                <HasPermission name="edit_admin_extension">
                                    <Tooltip device={device} />
                                </HasPermission>
                                <HasPermission name="manage_thirdparty_device" contextFilter={domainFilter}>
                                    <UpdateButton onSuccess={query.refetch} device={device} />
                                    <DeleteButton
                                        onSuccess={onSuccess}
                                        name={device.description}
                                        id={device.id}
                                        dataIdentifier={device.description}
                                    />
                                </HasPermission>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Fragment>
    );
};
